import { ActionIcon, Button, Checkbox, Collapse, Text, Tooltip } from '@mantine/core';

import { getHotkeyHandler, useElementSize } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  IconArrowCapsule,
  IconArrowMerge,
  IconCircleFilled,
  IconPinFilled,
  IconSquarePlus,
  IconLayoutBottombarCollapseFilled,
  IconLayoutNavbarCollapseFilled,
  IconPlayerPauseFilled,
  IconPlayerPlay,
  IconPlayerStopFilled,
  IconPlayerTrackNextFilled,
  IconPower,
  IconSearch,
  IconStrikethrough,
  IconTrash,
  IconX,
} from '@tabler/icons-react';
import clsx from 'clsx';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { previewExecutionIdAtom, prevStepStoredInTempFamily } from '~/atoms/atoms';
import {
  clearSelectionsAtom,
  erroredOperationAtom,
  expandOrCollapseAllAtom,
  selectedOpsIdsAtom,
  selectedStepsIdsAtom,
  stepSearchBarKeywordAtom,
  stepSearchBarVisibilityAtom,
} from '~/atoms/no-code-atoms';
import { createdDummyStepsAtom, recorderPausedAtom } from '~/atoms/recorder-atoms';
import { cn } from '~/common/string/classname';
import { TOUR_ID } from '~/components/tours/DraftEditorTour';
import { useDeleteOperations } from '~/hooks/mutations/operations/useDeleteOperations';
import { useSkipOperations } from '~/hooks/mutations/operations/useSkipOperations';
import { isStepSeparating } from '~/hooks/mutations/optimistic-handles';
import { useCombineSteps } from '~/hooks/mutations/steps/useCombineSteps';
import { useDeleteSteps } from '~/hooks/mutations/steps/useDeleteSteps';
import { useUpdateStepDescription } from '~/hooks/mutations/steps/useUpdateStepDescription';
import { useCreateDraftAndPreview } from '~/hooks/test/useCreateDraftAndPreview';
import { useSetAsTrunk } from '~/hooks/test/useSetAsTrunk';
import { useLiveStateManager } from '~/hooks/useLiveStateManager';
import { useResetRecorderAtoms } from '~/hooks/vm/useResetRecorder';
import { useSendVmCommand } from '~/hooks/vm/useSendVmCommand';
import { useStartRecorder } from '~/hooks/vm/useStartRecorder';
import { getDummyStep, spliceInStep } from '~/logic/optimistic-updates/step-insert';
import { api } from '~/trpc-extra/api';
import { CreateComponentConfirmationDialog } from '../../steps/step/CreateComponentConfirmationDialog';
import RecorderStatusDisplay from '../RecorderStatusDisplay';
import { ConvertToAIButton } from './ConvertToAIButton';
import { SearchBar } from './SearchBar';

/**
 * The control bar appears (stickily) above the list of steps in when editing a
 * test draft or viewing a published test
 *
 * @param searchText - The current search text used to filter steps.
 * @param setSearchText - Function to update the search text.
 * @param testId - The ID of the test currently being looked at.
 * @param filteredSteps - Array of steps that have been filtered based on the search text.
 * @param readonly - (default false) Optional boolean indicating if the control bar is in read-only mode.
 *
 * @returns JSX.Element representing the control bar.
 */
export default function ControlBar({
  readonly,
  testContentId,
  selectedTestContentVersion,
  className,
}: {
  testContentId: string;
  readonly?: boolean;
  selectedTestContentVersion?: string;
  className?: string;
}) {
  const trpcUtils = api.useUtils();

  const { data: testContent } = api.testContent.get.useQuery(
    { id: testContentId },
    { refetchOnMount: false, refetchOnWindowFocus: false, enabled: !!testContentId },
  );

  const startRecorder = useStartRecorder({ testContentId });
  const sendServerCommand = useSendVmCommand();

  const [searchBarVisible, setSearchBarVisible] = useAtom(stepSearchBarVisibilityAtom);
  const setSearchKeywordDebounced = useSetAtom(stepSearchBarKeywordAtom.debouncedValueAtom);

  // Used for converting component creation button to smaller on smaller control bar
  const { ref: leftControlBarRef, width: leftControlBarWidth } = useElementSize();

  // Use Ctrl + F to focus the search bar and ESC to close it
  useEffect(() => {
    const handler = getHotkeyHandler([
      [
        'mod+F',
        (e) => {
          if (!searchBarVisible) {
            e.preventDefault();
          }
          setSearchBarVisible((prev) => !prev);
        },
        { preventDefault: false },
      ],
      [
        'Escape',
        () => {
          setSearchBarVisible(false);
          setSearchKeywordDebounced({ type: 'immediate', value: '' });
        },
      ],
    ]);

    document.body.addEventListener('keydown', handler);

    return () => {
      document.body.removeEventListener('keydown', handler);
    };
  }, [setSearchKeywordDebounced, searchBarVisible]); // Make sure to add setKeyword to dependencies if it's coming from outside

  const [selectedStepsIds, setSelectedStepsIds] = useAtom(selectedStepsIdsAtom);
  const selectedSteps = useMemo(() => {
    const selectedStepsIdsSet = new Set(selectedStepsIds);
    return (testContent?.steps ?? []).filter((step) => selectedStepsIdsSet.has(step.id));
  }, [selectedStepsIds, testContent?.steps]);

  const [selectedOpsIds, setSelectedOpsIds] = useAtom(selectedOpsIdsAtom);
  const selectedOps = useMemo(() => {
    const selectedOpsIdsSet = new Set(selectedOpsIds);
    return testContent?.steps.flatMap((step) => step.operations).filter((op) => selectedOpsIdsSet.has(op.id)) ?? [];
  }, [selectedOpsIds, testContent?.steps]);

  const clearSelections = useSetAtom(clearSelectionsAtom);
  const [expandOrCollapseAll, setExpandOrCollapseAll] = useAtom(expandOrCollapseAllAtom);
  const [commonButtonDisabled, setCommonButtonDisabled] = useState(false);
  const isPolishing = isStepSeparating(selectedSteps.at(0)?.id || '');
  const resetRecorderAtoms = useResetRecorderAtoms(testContentId);
  const setErroredOperation = useSetAtom(erroredOperationAtom);
  const previewExecutionId = useAtomValue(previewExecutionIdAtom);
  const recorderPaused = useAtomValue(recorderPausedAtom);
  const setCreatedDummySteps = useSetAtom(createdDummyStepsAtom);
  const { vmStatus, updateLiveState, recorderInitiator, recorderState } = useLiveStateManager();
  const createDraftAndPreview = useCreateDraftAndPreview({ testContentId });

  const [isCancellingExecution, setIsCancellingExecution] = useState(false);
  useEffect(() => setIsCancellingExecution(false), [vmStatus?.state]);

  const selectedStepsAndOpsLabel = `${selectedSteps.length === 0 ? '' : `step${selectedSteps.length > 1 ? 's' : ''}`}${
    selectedSteps.length > 0 && selectedOps.length > 0 ? ' and ' : ''
  }${selectedOps.length === 0 ? '' : `action${selectedOps.length > 1 ? 's' : ''}`}`;

  // Derive the step id that should be used for prevStepStoredInTemp.
  // We look for a step containing an operation that matches the current vmStatus's operation id (or source operation id).
  const derivedStepId = useMemo(() => {
    const currentStepId = vmStatus?.executionState?.currentStepId;
    if (currentStepId) {
      return currentStepId;
    }

    // Fallback to the recorder state stepId if the test data is not available
    if (!testContent) {
      return recorderState?.stepId || recorderInitiator?.location?.stepId || '';
    }

    const foundStep = testContent?.steps.find((step) => {
      // Check if any of the operations inside this step match the current execution operation id
      // or if the step itself matches the operation id (for cases where the step ID is used as the operation id)
      const isReplayingOp =
        step.operations.some(
          (op) =>
            op.id === vmStatus?.executionState?.operationId ||
            op.id === vmStatus?.executionState?.stepSourceOperationId,
        ) || vmStatus?.executionState?.operationId === step.id;

      return isReplayingOp;
    });

    // Use the found step's id if available, otherwise fall back to recorder state
    return foundStep ? foundStep.id : recorderState?.stepId || recorderInitiator?.location?.stepId || '';
  }, [
    testContent,
    vmStatus?.executionState?.currentStepId,
    vmStatus?.executionState?.operationId,
    vmStatus?.executionState?.stepSourceOperationId,
    recorderState?.stepId,
    recorderInitiator,
  ]);

  const prevStepParams = useMemo(
    () => ({
      stepId: derivedStepId,
      executionId: previewExecutionId || '',
    }),
    [derivedStepId, previewExecutionId],
  );
  const key = `${prevStepParams.stepId}_${prevStepParams.executionId}`;
  const prevStepStoredInTemp = useAtomValue(prevStepStoredInTempFamily(key));

  const replayPinCursorStepId = derivedStepId;

  // AI step execution sometimes uses stepId as operationId. Only pass operationId if it is a real operation.
  const replayPinCursorOperationId = useMemo(() => {
    const operationId = vmStatus?.executionState?.operationId;
    if (!operationId || !replayPinCursorStepId) {
      return undefined;
    }

    const step = testContent?.steps.find((s) => s.id === replayPinCursorStepId);
    return step?.operations.some((op) => op.id === operationId) ? operationId : undefined;
  }, [vmStatus?.executionState?.operationId, replayPinCursorStepId, testContent?.steps]);

  const isStepsAdjacent = useMemo(() => {
    if (selectedSteps.length <= 1) {
      return false;
    }
    // Sort selected steps by order
    const sortedSteps = selectedSteps.sort((a, b) => a.stepOrder - b.stepOrder);

    const indexOfFirstSelectedStep = (testContent?.steps || []).findIndex(
      (step) => step.stepOrder === sortedSteps[0].stepOrder,
    );
    const indexOfLastSelectedStep = (testContent?.steps || []).findIndex(
      (step) => step.stepOrder === sortedSteps.at(-1)?.stepOrder,
    );

    const indexGap = indexOfLastSelectedStep - indexOfFirstSelectedStep;

    // Check if the steps are adjacent
    return indexGap === sortedSteps.length - 1;
  }, [selectedSteps.length]);

  const isStepsModeSame = useMemo(() => {
    return selectedSteps.every((step) => step.mode === selectedSteps[0].mode);
  }, [selectedSteps.length]);

  // Check if selected operations are adjacent within their respective steps
  const areOperationsAdjacent = useMemo(() => {
    if (selectedOps.length <= 1) {
      return false;
    }

    const opsByStep = Object.groupBy(selectedOps, (op) => op.testStep?.id || '');
    const stepIds = Object.keys(opsByStep);

    // Only allow adjacency check if all operations are within the same step
    if (stepIds.length > 1) {
      return false;
    }

    // Check if operations within the single step are adjacent
    const stepId = stepIds[0];
    const stepOps = opsByStep[stepId]?.sort((a, b) => a.order - b.order) || [];

    if (stepOps.length <= 1) {
      return false;
    }

    const allStepOps = testContent?.steps.find((step) => step.id === stepId)?.operations || [];

    const firstSelectedOpIndex = allStepOps.findIndex((op) => op.id === stepOps[0].id);
    const lastSelectedOpIndex = allStepOps.findIndex((op) => op.id === stepOps.at(-1)?.id);

    const indexGap = lastSelectedOpIndex - firstSelectedOpIndex;
    return indexGap === stepOps.length - 1;
  }, [selectedOps.length]);

  // Check if we can create a component from selected items
  const canCreateComponent = useMemo(() => {
    const hasSelections = selectedSteps.length > 0 || selectedOps.length > 0;
    const hasMixedSelections = selectedSteps.length > 0 && selectedOps.length > 0;
    const hasAdjacentSteps = selectedSteps.length > 1 && isStepsAdjacent;
    const hasAdjacentOps = selectedOps.length > 1 && areOperationsAdjacent;
    const hasSingleStep = selectedSteps.length === 1 && selectedOps.length === 0;
    const hasSingleOp = selectedSteps.length === 0 && selectedOps.length === 1;

    return hasSelections && !hasMixedSelections && (hasAdjacentSteps || hasAdjacentOps || hasSingleStep || hasSingleOp);
  }, [selectedSteps.length, selectedOps.length, isStepsAdjacent, areOperationsAdjacent]);

  const isStepsAndOpsSkipModeSame = useMemo(() => {
    return [...selectedSteps.map((step) => step.skip), ...selectedOps.map((op) => op.skip)].every(
      (skip, _, arr) => skip === arr[0],
    );
  }, [selectedSteps, selectedOps]);

  const allStepsAndOpsSelected = useMemo(() => {
    return (
      selectedSteps.length === (testContent?.steps.length ?? 0) &&
      selectedOps.length === (testContent?.steps.reduce((acc, step) => acc + step.operations.length, 0) ?? 0)
    );
  }, [selectedSteps.length, selectedOps.length, testContent?.steps]);

  // setting up mutation hooks for deleteSteps, updateComponent, detachComponent
  const { mutate: deleteSteps, isPending: isDeletingSteps } = useDeleteSteps({
    testContentId,
  });

  const { mutate: deleteOperations } = useDeleteOperations({ testContentId });

  const { mutate: skipSteps, isPending: isSkippingSteps } = api.testStep.updateMany.useMutation({
    onMutate: () => {
      notifications.show({ message: `${selectedSteps[0].skip ? 'Unskipping' : 'Skipping'} selected steps...` });
    },
    onSuccess: () => {
      notifications.show({ message: 'Steps updated', color: 'teal' });
      trpcUtils.testContent.get.invalidate({ id: testContentId }).catch(() => undefined);
      clearSelections();
    },
    onError: () => {
      notifications.show({ message: 'Failed to skip/unskip the steps', color: 'red' });
    },
  });

  const isBlockingActionLoading = isDeletingSteps || isSkippingSteps;

  const { mutate: skipOperations } = useSkipOperations({ testContentId });
  const { mutateAsync: combineSteps } = useCombineSteps({ testContentId });

  const { updateStepDescription } = useUpdateStepDescription({ testContentId });

  const handleCombineSteps = useCallback(() => {
    modals.openConfirmModal({
      title: 'Please confirm',
      children: <span>This will combine the selected steps.</span>,
      labels: { confirm: 'Combine Steps', cancel: 'Cancel' },
      centered: true,
      onConfirm: async () => {
        await combineSteps({ stepIds: selectedSteps.map((step) => step.id) });
        await updateStepDescription({ stepId: selectedSteps[0]?.id ?? '' });
      },
    });
  }, [selectedSteps, combineSteps, updateStepDescription]);

  const handleDeleteSelections = useCallback(async () => {
    modals.closeAll();
    const modalProps =
      (
        selectedSteps.some(
          (step) =>
            step.stepOrder === 0 && step.operations.at(0)?.navigationOp && testContent?.test.testType !== 'MOBILE_APP',
        )
      ) ?
        {
          title: 'Delete Initial Navigation',
          children: <Text>Are you sure you want to delete the initial navigation step?</Text>,
          labels: { confirm: 'Delete', cancel: 'Cancel' },
        }
      : {
          title: 'Please confirm',
          children: <Text>{`This will permanently delete the ${selectedStepsAndOpsLabel}.`}</Text>,
          labels: { confirm: `Delete ${selectedStepsAndOpsLabel}`, cancel: 'Cancel' },
        };
    modals.openConfirmModal({
      ...modalProps,
      confirmProps: { color: 'red' },
      centered: true,
      onConfirm: async () => {
        if (selectedOps.length !== 0) {
          deleteOperations({ operationIds: selectedOps.map((op) => op.id) });
        }
        if (selectedSteps.length !== 0) {
          deleteSteps({ stepIds: selectedSteps.map((step) => step.id) });
        }
        clearSelections();
      },
    });
  }, [
    selectedSteps,
    selectedOps,
    selectedStepsAndOpsLabel,
    deleteSteps,
    deleteOperations,
    clearSelections,
    testContent,
  ]);

  const handleSkipSelections = useCallback(() => {
    if (selectedSteps.some((step) => step.stepOrder === 0)) {
      notifications.show({ message: 'The initial navigation step cannot be skipped', color: 'red' });
    } else {
      if (selectedOps.length !== 0) {
        skipOperations({
          operationIds: selectedOps.map((op) => op.id),
          skip: !selectedOps.at(0)?.skip,
        });
      }
      if (selectedSteps.length !== 0) {
        skipSteps({
          stepIds: selectedSteps.map((step) => step.id),
          data: {
            skip: !selectedSteps.at(0)?.skip,
          },
        });
      }
      // If any operation in the selected steps or ops is currently errored, clear it
      const allOperationIds = [...selectedSteps.flatMap(({ operations }) => operations), ...selectedOps].map(
        ({ id }) => id,
      );
      setErroredOperation((cur) => (allOperationIds.includes(cur?.id ?? '') ? undefined : cur));
    }
  }, [selectedSteps, selectedOps, skipSteps, skipOperations, setErroredOperation]);

  const handleCreateComponentClick = useCallback(() => {
    const selectedItemsCount = selectedSteps.length + selectedOps.length;
    const selectedItemType =
      selectedSteps.length > 0 && selectedOps.length > 0 ? 'mixed'
      : selectedSteps.length > 0 ? 'steps'
      : 'operations';

    modals.open({
      title: 'Create Component',
      size: 'lg',
      children: (
        <CreateComponentConfirmationDialog
          selectedItemsCount={selectedItemsCount}
          selectedItemType={selectedItemType}
          testContentId={testContentId}
          onCancel={() => modals.closeAll()}
        />
      ),
      centered: true,
    });
  }, [selectedSteps.length, selectedOps.length, testContentId]);

  useEffect(() => {
    setCommonButtonDisabled(isPolishing || selectedSteps.length === 0 || isBlockingActionLoading || !!readonly);
  }, [isPolishing, selectedSteps, isBlockingActionLoading, readonly]);

  const insertAfterStepOrder = useMemo(() => {
    return testContent?.steps.at(-1)?.stepOrder || 0;
  }, [testContent?.steps]);

  const handleRecordClick = async () => {
    const lastStep = testContent?.steps.at(-1);

    // Check if the last step is an empty AI_FALLBACK step that we can reuse
    const canReuseLastStep =
      lastStep?.mode === 'AI_FALLBACK' && lastStep.description === '' && lastStep.operations.length === 0;

    const { recordLocation, updatedSteps } =
      canReuseLastStep ?
        {
          // Reuse the existing empty step
          recordLocation: {
            type: 'step-id',
            order: lastStep.stepOrder,
            stepId: lastStep.id,
          } as const,
          updatedSteps: testContent?.steps || [],
        }
      : (() => {
          // Create a new dummy step
          const dummyStep = getDummyStep({
            description: '',
            order: insertAfterStepOrder + 1,
            testContentId,
            mode: 'AI_FALLBACK',
          });

          // Track this newly created dummy step for potential cleanup
          setCreatedDummySteps((prev) => [...prev, dummyStep.id]);

          return {
            recordLocation: {
              type: 'insert-step',
              order: insertAfterStepOrder + 1,
              stepId: dummyStep.id,
            } as const,
            updatedSteps: spliceInStep(testContent?.steps || [], dummyStep),
          };
        })();

    // Cancel outgoing fetches (so they don't overwrite our optimistic update)
    await trpcUtils.testContent.get.cancel({ id: testContentId });

    startRecorder({
      executionType: 'record',
      location: recordLocation,
    });

    trpcUtils.testContent.get.setData({ id: testContentId }, (old) => {
      return old ? { ...old, steps: updatedSteps } : old;
    });
  };

  const setAsTrunk = useSetAsTrunk();

  return (
    <div
      className={cn(
        'w-full rounded-lg border-[1px] border-gray-300 shadow-md backdrop-blur-lg dark:border-gray-600',
        recorderPaused ? 'bg-yellow-500/20 dark:bg-yellow-900/20' : 'bg-transparent',
        className,
      )}
    >
      <div className="flex w-full items-center justify-between gap-2 px-2 py-1">
        <div className="flex grow items-center">
          {/* Left-side group of buttons */}
          {(
            searchBarVisible // prioritize search bar over all buttons
          ) ?
            <SearchBar
              autoFocus
              className="mr-3 w-full"
              disabled={isBlockingActionLoading}
            />
          : (
            selectedSteps.length === 0 && selectedOps.length === 0 // if no steps or ops are selected, show the preview start/stop buttons
          ) ?
            <div className="flex items-center">
              {recorderInitiator ?
                <div className="flex items-center gap-3 pl-3">
                  <Tooltip
                    key="close-virtual-browser"
                    label="Close virtual browser"
                    disabled={isBlockingActionLoading}
                  >
                    <ActionIcon
                      variant="transparent"
                      size="compact-sm"
                      onClick={async () => {
                        modals.openConfirmModal({
                          title: (
                            <div className="flex items-center gap-2 text-lg text-red-600 dark:text-red-500">
                              <IconPower className="text-red-600 dark:text-red-500" />
                              <span>Close virtual browser</span>
                            </div>
                          ),
                          children: (
                            <span>
                              This will cause the website state to be reset. Are you sure you want to close the browser?
                            </span>
                          ),
                          labels: { confirm: 'Close', cancel: 'Cancel' },
                          centered: true,
                          onConfirm: async () => await resetRecorderAtoms(),
                          confirmProps: { color: 'red' },
                        });
                      }}
                      disabled={isBlockingActionLoading || prevStepStoredInTemp === false}
                      className="flex-shrink-0 hover:bg-gray-100 dark:hover:bg-gray-800"
                    >
                      <IconPower
                        size={22}
                        className={clsx(
                          vmStatus?.state === 'replaying' ?
                            'text-gray-700 dark:text-gray-300'
                          : 'text-red-600 dark:text-red-500',
                          'enabled:hover:bg-red-600/20 disabled:text-red-100/20 dark:enabled:hover:text-white/80 dark:disabled:text-white/20',
                        )}
                      />
                    </ActionIcon>
                  </Tooltip>
                  {recorderInitiator?.executionType === 'record' && vmStatus?.state === 'ready' && (
                    <Tooltip
                      label="Run from the current cursor to the end"
                      disabled={isBlockingActionLoading}
                    >
                      <ActionIcon
                        id={TOUR_ID.PREVIEW_BUTTON}
                        size="compact-sm"
                        variant="transparent"
                        disabled={isBlockingActionLoading || prevStepStoredInTemp === false || recorderPaused}
                        onClick={() => {
                          sendServerCommand({
                            type: 'switch-mode',
                            mode: 'preview',
                            cursorStepId: testContent?.steps.at(-1)?.id || '',
                            cursorStepOrder: insertAfterStepOrder + 1,
                            cursorOperation: recorderState?.operation,
                          });
                          updateLiveState((prev) => ({
                            ...prev,
                            vmStatus: {
                              state: 'ready',
                              humanState: 'ready',
                              aliasesToUrls: vmStatus?.aliasesToUrls,
                            },
                            recorderInitiator: { ...prev.recorderInitiator, executionType: 'preview' },
                          }));
                        }}
                        className="animate-fadeIn text-green-600 hover:text-green-700 disabled:bg-transparent disabled:text-black/20 dark:text-gray-300 dark:enabled:text-gray-300 dark:enabled:hover:text-white/80 dark:disabled:text-white/20"
                      >
                        <IconPlayerTrackNextFilled size={21} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                  {vmStatus?.state === 'replaying' && (
                    <Tooltip
                      label="Stop the execution but preserves the current state"
                      disabled={isBlockingActionLoading}
                    >
                      <ActionIcon
                        color="red"
                        size="compact-sm"
                        variant="transparent"
                        onClick={async () => {
                          if (isCancellingExecution) {
                            notifications.show({
                              message: 'Cancelling execution in progress',
                              color: 'yellow',
                            });
                            return;
                          }
                          setIsCancellingExecution(true);
                          sendServerCommand({
                            type: 'cancel-replay',
                          });
                          notifications.show({
                            message: 'Cancelling the execution... It might take a few moments to take effect',
                            color: 'blue',
                          });
                        }}
                        disabled={isBlockingActionLoading || prevStepStoredInTemp === false}
                        className="animate-fadeIn hover:bg-red-100 dark:hover:bg-red-900/30"
                      >
                        <IconPlayerStopFilled size={22} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                </div>
              : <div className="flex items-center gap-2">
                  {!selectedTestContentVersion && !readonly && (
                    <Tooltip
                      key="preview-to-end"
                      label="Preview to the end"
                      disabled={isBlockingActionLoading}
                    >
                      <Button
                        leftSection={
                          <IconPlayerPlay
                            size={14}
                            className="text-white-500 enabled:hover:bg-green-500/20 disabled:opacity-20"
                          />
                        }
                        size="compact-sm"
                        variant="default"
                        onClick={async () => {
                          if (readonly) {
                            await createDraftAndPreview();
                          } else {
                            startRecorder({
                              executionType: 'preview',
                              location: {
                                type: 'step-id',
                                order: insertAfterStepOrder + 1,
                                stepId: testContent?.steps.at(-1)?.id || '',
                              },
                            });
                          }
                        }}
                        disabled={isBlockingActionLoading}
                      >
                        Preview
                      </Button>
                    </Tooltip>
                  )}
                  {!!selectedTestContentVersion && (
                    <Button
                      size="compact-sm"
                      variant="default"
                      leftSection={<IconArrowCapsule size={16} />}
                      onClick={() => setAsTrunk({ testContentId })}
                    >
                      Revert to this version
                    </Button>
                  )}

                  {!readonly && (
                    <Tooltip
                      label="Record from the end"
                      disabled={isBlockingActionLoading}
                    >
                      <Button
                        size="compact-sm"
                        variant="default"
                        disabled={!!recorderInitiator}
                        leftSection={
                          <IconCircleFilled
                            size={8}
                            color={recorderInitiator ? 'gray' : 'red'}
                          />
                        }
                        onClick={handleRecordClick}
                      >
                        Record
                      </Button>
                    </Tooltip>
                  )}
                </div>
              }
            </div>
          : <div
              className="flex h-full w-full items-center gap-2"
              ref={leftControlBarRef}
            >
              <Tooltip
                label="Select all steps and actions"
                className="pr-1"
              >
                <Checkbox
                  checked={allStepsAndOpsSelected}
                  classNames={{
                    input: clsx({
                      'border-gray-600 dark:border-gray-400 border-[2px]': !allStepsAndOpsSelected,
                    }),
                  }}
                  onChange={(e) => {
                    setSelectedStepsIds(e.target.checked ? (testContent?.steps.map((step) => step.id) ?? []) : []);
                    setSelectedOpsIds(
                      e.target.checked ?
                        (testContent?.steps.flatMap((step) => step.operations.map((op) => op.id)) ?? [])
                      : [],
                    );
                  }}
                  data-testid="toggle-select-all"
                />
              </Tooltip>
              <Tooltip
                label={
                  !canCreateComponent ?
                    'Select adjacent actions or steps to create a component'
                  : `Create component from ${selectedSteps.length + selectedOps.length} selected ${
                      selectedSteps.length > 0 && selectedOps.length > 0 ? 'items'
                      : selectedSteps.length > 0 ? 'steps'
                      : 'actions'
                    }`
                }
              >
                {leftControlBarWidth < 240 ?
                  <ActionIcon
                    onClick={handleCreateComponentClick}
                    disabled={!canCreateComponent || readonly}
                    variant="subtle"
                    size="md"
                  >
                    <IconSquarePlus size={20} />
                  </ActionIcon>
                : <Button
                    onClick={handleCreateComponentClick}
                    size="compact-sm"
                    disabled={!canCreateComponent || readonly}
                    styles={(theme) => ({
                      root: {
                        backgroundColor: !canCreateComponent || readonly ? theme.colors.gray[2] : theme.colors.blue[6],
                      },
                    })}
                  >
                    Component
                  </Button>
                }
              </Tooltip>
              <ConvertToAIButton
                readonly={readonly}
                testContent={testContent}
              />
            </div>
          }
        </div>

        {/* Right-side group of buttons */}
        <div className="flex items-center gap-3 rounded-md py-1">
          {searchBarVisible || (selectedSteps.length === 0 && selectedOps.length === 0) ?
            <>
              <Tooltip label={expandOrCollapseAll == 'expand' ? 'Collapse All' : 'Expand All'}>
                <ActionIcon
                  variant="subtle"
                  size="md"
                  color="gray"
                  title={expandOrCollapseAll == 'expand' ? 'Collapse All' : 'Expand All'}
                  onClick={() => {
                    setExpandOrCollapseAll((prev) => (prev == 'expand' ? 'collapse' : 'expand'));
                  }}
                >
                  {expandOrCollapseAll == 'expand' ?
                    <IconLayoutBottombarCollapseFilled size={20} />
                  : <IconLayoutNavbarCollapseFilled size={20} />}
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Cmd + F">
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="md"
                  onClick={() => setSearchBarVisible((prev) => !prev)}
                >
                  {searchBarVisible ?
                    <IconX size={18} />
                  : <IconSearch size={18} />}
                </ActionIcon>
              </Tooltip>
            </>
          : <>
              {selectedSteps.length > 1 && (
                <Tooltip
                  label={
                    !isStepsAdjacent || !isStepsModeSame ?
                      'Cannot combine steps that are not adjacent or have different modes'
                    : `Combine ${selectedSteps.length} steps' content`
                  }
                >
                  <ActionIcon
                    variant="subtle"
                    size="md"
                    disabled={commonButtonDisabled || !isStepsAdjacent || !isStepsModeSame}
                    onClick={() => handleCombineSteps()}
                    className="text-gray-600 disabled:opacity-40 dark:text-gray-400"
                  >
                    <IconArrowMerge size={20} />
                  </ActionIcon>
                </Tooltip>
              )}
              <Tooltip label={`Delete ${selectedStepsAndOpsLabel}`}>
                <ActionIcon
                  variant="subtle"
                  size="md"
                  disabled={isBlockingActionLoading || readonly}
                  onClick={handleDeleteSelections}
                  className="text-gray-600 disabled:opacity-40 dark:text-gray-400"
                >
                  <IconTrash size={20} />
                </ActionIcon>
              </Tooltip>
              <Tooltip
                label={
                  isStepsAndOpsSkipModeSame ?
                    `${selectedSteps.at(0)?.skip ? 'Unskip' : 'Skip'} selected ${selectedStepsAndOpsLabel}`
                  : `Cannot skip a mix of skipped and unskipped ${selectedStepsAndOpsLabel}`
                }
              >
                <ActionIcon
                  variant="subtle"
                  size="md"
                  color="gray"
                  onClick={handleSkipSelections}
                  disabled={!isStepsAndOpsSkipModeSame}
                  className="text-gray-600 disabled:opacity-40 dark:text-gray-400"
                >
                  <IconStrikethrough size={20} />
                </ActionIcon>
              </Tooltip>
            </>
          }
        </div>
      </div>

      <Collapse in={recorderInitiator?.executionType !== undefined}>
        <div className="flex h-14 items-center border-t border-gray-300 px-4 py-4 dark:border-gray-600">
          <div className="ml-1 flex w-full items-center justify-between gap-2 text-xs font-medium text-gray-800 dark:text-gray-300">
            <RecorderStatusDisplay
              testContentId={testContentId}
              isCancellingExecution={isCancellingExecution}
            />
            {!!recorderInitiator &&
              (vmStatus?.state === 'replaying' ?
                // During replay - show pin button to stop and record from current position
                <Tooltip label="Stop replay and record from here">
                  <ActionIcon
                    color="gray.7"
                    size="compact-sm"
                    variant="transparent"
                    onClick={() => {
                      sendServerCommand({
                        type: 'stop-replay-and-record',
                        cursorStepId: replayPinCursorStepId,
                        cursorOperationId: replayPinCursorOperationId,
                      });
                    }}
                    disabled={isBlockingActionLoading || !replayPinCursorStepId}
                    className="flex-shrink-0"
                  >
                    <IconPinFilled size={22} />
                  </ActionIcon>
                </Tooltip>
              : recorderInitiator.executionType === 'record' && vmStatus?.state === 'ready' ?
                // During recording (ready state) - show pause button
                <Tooltip
                  label={recorderPaused ? 'Resume recording' : 'Pause recording'}
                  disabled={isBlockingActionLoading}
                >
                  <ActionIcon
                    color="gray.7"
                    size="compact-sm"
                    variant="transparent"
                    onClick={() => {
                      sendServerCommand({ type: 'toggle-pause' });
                    }}
                    disabled={isBlockingActionLoading || prevStepStoredInTemp === false}
                    className="flex-shrink-0"
                  >
                    {recorderPaused ?
                      <IconCircleFilled
                        size={18}
                        className="text-amber-600"
                      />
                    : <IconPlayerPauseFilled size={22} />}
                  </ActionIcon>
                </Tooltip>
              : null)}
          </div>
        </div>
      </Collapse>
    </div>
  );
}
